import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductionNoteService } from '@shared/services/production-note.service';
import { LocalStorageService } from '@shared/services/storage.service';
import { TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { EMPTY, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { IBatch } from '../../mes/mes-batch/mes-batch-service';
import { IConfiguration, IInfoColumn, IOrderPosition, IStatusColumn, TerminalStatusState } from '../models/production-plan.models';
import { ProductionPlanConfigurationService } from '../production-plan-configuration.service';
import { IProductionPlanInfoDialogueColumn, ProductionPlanInfoDialogueComponent } from '../production-plan-info-dialogue/production-plan-info-dialogue.component';
import { ProductionPlanNotesDialogComponent } from '../production-plan-notes-dialog/production-plan-notes-dialog.component';
import { ProductionPlanService } from '../production-plan.service';

interface IInfoColumnView extends IInfoColumn {
	width: number;
}

interface IStatusColumnView extends IStatusColumn {
	width: number;
}

@Component({
	selector: 'app-production-plan-view',
	templateUrl: './production-plan-view.component.html',
	styleUrl: './production-plan-view.component.scss',
	providers: [ProductionPlanService],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionPlanViewComponent implements OnInit, OnDestroy {
	@ViewChild('headerWrapper') headerWrapper: ElementRef;
	// A plural query, unlike @ViewChild, exposes `.changes` - needed because the header/viewport are
	// behind `@if (!isLoadingPlan)` and isLoadingPlan flips to true synchronously during ngOnInit
	// (before ngAfterViewInit ever runs), so a single @ViewChild(CdkVirtualScrollViewport) read in
	// ngAfterViewInit would be undefined on the first (and only) load - the plan reliably hasn't
	// finished loading yet. Reacting to `.changes` instead re-attempts the moment it actually appears.
	@ViewChildren(CdkVirtualScrollViewport) viewportQuery: QueryList<CdkVirtualScrollViewport>;

	private destroy$ = new Subject<void>();
	private originalItems: IOrderPosition[] = [];

	columns: any[] = [];
	items: IOrderPosition[] = [];
	isLoadingPlan = false;
	sortColumn = '';
	sortDirection: 'asc' | 'desc' | '' = '';
	showCompleted = true;
	showInProgress = true;
	showNotStarted = true;
	selectedBatchId = '';
	selectedOrderNumber = '';
	tableWidth = 0;
	allStatusTerminalIds: string[] = [];
	batches: IBatch[] = [];
	orderNumbers: string[] = [];
	planLookup: Map<string, IConfiguration> = new Map();
	planName = '';
	terminalNames: Map<string, string> = new Map();

	trackByOrderPosition = (_: number, item: IOrderPosition) => `${item.orderNumber}-${item.position}`;

	constructor(
		protected service: ProductionPlanService,
		protected noteService: ProductionNoteService,
		private configService: ProductionPlanConfigurationService,
		private dialog: MatDialog,
		private route: ActivatedRoute,
		private localStorageService: LocalStorageService,
		private terminalService: TerminalAdministrationService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.terminalService
			.getTerminals()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) =>
				response.result.forEach((terminal) => {
					if (terminal.id) {
						this.terminalNames.set(terminal.id, `(${terminal.alias}) ${terminal.name}`);
					}
				}),
			);

		this.configService
			.get()
			.pipe(takeUntil(this.destroy$))
			.subscribe((plans) => plans.forEach((p) => this.planLookup.set(p.id, p)));

		this.service.orderPositions$.pipe(takeUntil(this.destroy$)).subscribe((positionsMap) => {
			const positions = Array.from(positionsMap.values());
			this.originalItems = positions;
			this.orderNumbers = [...new Set(positions.map((item) => String(item.orderNumber)))].sort((a, b) => Number(a) - Number(b));
			this.setSortAndFilter();
		});

		this.service.orderPositionAdd$.pipe(takeUntil(this.destroy$)).subscribe((newPosition) => {
			this.addBecomeSortedAndFiltered(newPosition);
		});

		this.service.orderPositionRemove$.pipe(takeUntil(this.destroy$)).subscribe((removedPosition) => {
			this.removeFromSortedAndFiltered(removedPosition);
		});

		this.service.batches$.pipe(takeUntil(this.destroy$)).subscribe((batches) => {
			this.batches = [...batches].sort((a, b) => this.service.getBatchName(a.id).localeCompare(this.service.getBatchName(b.id)));
		});

		this.service.terminalStatusRowStateChanged$.pipe(takeUntil(this.destroy$)).subscribe((terminalStatusRowStateChanged) => {
			this.updateRowVisibility(terminalStatusRowStateChanged.orderNumber, terminalStatusRowStateChanged.position);
		});

		this.service.terminalStatusRowsLoaded$.pipe(takeUntil(this.destroy$)).subscribe((rowKeys) => {
			this.updateLoadedRowVisibilities(rowKeys);
		});

		this.route.paramMap
			.pipe(
				map((params) => params.get('configId')),
				distinctUntilChanged(),
				switchMap((configId) => {
					if (!configId) {
						this.resetPlanState();
						return EMPTY;
					}
					return this.loadPlan(configId);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe({
				error: (err) => console.error('Failed to load production plan:', err),
			});
	}

	private isStatusVisible(status: TerminalStatusState): boolean {
		if (status === TerminalStatusState.Irrelevant) return true;
		if (status === TerminalStatusState.Completed) return this.showCompleted;
		if (status === TerminalStatusState.InProgress) return this.showInProgress;
		if (status === TerminalStatusState.Pending || status === TerminalStatusState.Ready) {
			return this.showNotStarted;
		}
		return true;
	}

	private loadPlan(configId: string) {
		this.isLoadingPlan = true;
		this.resetPlanState();
		this.service.configurationId = configId;
		return this.service.loadConfiguration().pipe(
			switchMap((config) => {
				this.buildColumns(config);
				return this.service.loadInitialData();
			}),
			finalize(() => {
				this.isLoadingPlan = false;
			}),
		);
	}

	private resetPlanState(): void {
		this.columns = [];
		this.planName = '';
		this.items = [];
		this.originalItems = [];
		this.sortColumn = '';
		this.sortDirection = '';
		this.selectedBatchId = '';
		this.selectedOrderNumber = '';
		this.tableWidth = 0;
		this.allStatusTerminalIds = [];
		this.batches = [];
		this.orderNumbers = [];
		this.showCompleted = true;
		this.showInProgress = true;
		this.showNotStarted = true;
	}

	ngAfterViewInit(): void {
		this.viewportQuery.changes
			.pipe(
				startWith(this.viewportQuery),
				map((query: QueryList<CdkVirtualScrollViewport>) => query.first),
				switchMap((viewport: CdkVirtualScrollViewport | undefined) => {
					if (!viewport) {
						return EMPTY;
					}
					return viewport.elementScrolled().pipe(map(() => viewport));
				}),
				takeUntil(this.destroy$),
			)
			.subscribe((viewport) => {
				const scrollLeft = viewport.elementRef.nativeElement.scrollLeft;
				this.headerWrapper.nativeElement.scrollLeft = scrollLeft;
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildColumns(config: IConfiguration): void {
		this.planName = config.description || config.alias;
		const infoColumns: IInfoColumnView[] = config.infoColumns.map((column) => ({
			...column,
			width: 140,
		}));

		this.setInfoColumnWidths(infoColumns);
		this.restoreSortAndFilter();

		const statusColumns: Array<IStatusColumnView & { isStatus: boolean }> = [
			...config.statusColumns.map((column) => ({
				...column,
				width: 160,
				isStatus: true,
			})),
		];

		this.allStatusTerminalIds = statusColumns.flatMap((column) => column.terminalIds).filter((id, index, arr) => id && arr.indexOf(id) === index);

		this.columns = [
			...infoColumns.map((column) => ({
				...column,
				isStatus: false,
			})),
			...statusColumns,
		];

		this.tableWidth = this.columns.reduce((sum, column) => sum + column.width, 0);
	}

	private setInfoColumnWidths(infoColumns: IInfoColumnView[]): void {
		infoColumns.forEach((column, index) => {
			const key = `${this.service.configurationId}#Columns#${index}`;
			const storedWidthStr = this.localStorageService.get(key);
			const storedWidth = Number(storedWidthStr);
			if (storedWidth && !Number.isNaN(storedWidth) && storedWidth > 0 && Number.isFinite(storedWidth)) {
				column.width = storedWidth;
			}
		});
	}

	private restoreSortAndFilter(): void {
		const id = this.service.configurationId;

		const col = this.localStorageService.get(`${id}#Sort#column`);
		const dir = this.localStorageService.get(`${id}#Sort#direction`);
		const batchId = this.localStorageService.get(`${id}#Filter#batchId`);
		const orderNumber = this.localStorageService.get(`${id}#Filter#orderNumber`);
		if (col) this.sortColumn = col;
		if (dir === 'asc' || dir === 'desc') this.sortDirection = dir;
		this.selectedBatchId = batchId ?? '';
		this.selectedOrderNumber = orderNumber ?? '';

		const completed = this.localStorageService.get(`${id}#Filter#showCompleted`);
		const inProgress = this.localStorageService.get(`${id}#Filter#showInProgress`);
		const notStarted = this.localStorageService.get(`${id}#Filter#showNotStarted`);

		this.showCompleted = completed !== 'false';
		this.showInProgress = inProgress !== 'false';
		this.showNotStarted = notStarted !== 'false';
	}

	private saveSortAndFilter(): void {
		const id = this.service.configurationId;
		if (!id) return;
		this.localStorageService.set(`${id}#Sort#column`, this.sortColumn);
		this.localStorageService.set(`${id}#Sort#direction`, this.sortDirection);
		this.localStorageService.set(`${id}#Filter#batchId`, this.selectedBatchId);
		this.localStorageService.set(`${id}#Filter#orderNumber`, this.selectedOrderNumber);
		this.localStorageService.set(`${id}#Filter#showCompleted`, String(this.showCompleted));
		this.localStorageService.set(`${id}#Filter#showInProgress`, String(this.showInProgress));
		this.localStorageService.set(`${id}#Filter#showNotStarted`, String(this.showNotStarted));
	}

	protected applySortAndFilter(): void {
		this.saveSortAndFilter();
		this.setSortAndFilter();
	}

	private setSortAndFilter(): void {
		this.items = this.useFilter(this.getSortedItems());
	}

	private getSortedItems(): IOrderPosition[] {
		let result: IOrderPosition[];
		if (this.sortDirection === '') {
			result = [...this.originalItems];
		} else {
			result = [...this.originalItems].sort((left, right) => this.sort(left, right));
		}
		return result;
	}

	private sort(left: IOrderPosition, right: IOrderPosition): number {
		const valA = this.sortColumn.split('.').reduce((acc, key) => acc?.[key], left as any) ?? '';
		const valB = this.sortColumn.split('.').reduce((acc, key) => acc?.[key], right as any) ?? '';
		let comparison = 0;
		if (typeof valA === 'number' && typeof valB === 'number') {
			comparison = valA - valB;
		} else {
			comparison = String(valA).localeCompare(String(valB));
		}
		return this.sortDirection === 'asc' ? comparison : -comparison;
	}

	private useFilter(items: IOrderPosition[]): IOrderPosition[] {
		return items.filter((item) => this.matchesCurrentFilter(item));
	}

	private matchesCurrentFilter(item: IOrderPosition, status = this.getRowStatus(item)): boolean {
		if (this.selectedBatchId && item.batchId !== this.selectedBatchId) return false;
		if (this.selectedOrderNumber && String(item.orderNumber) !== this.selectedOrderNumber) {
			return false;
		}
		return this.isStatusVisible(status);
	}

	private addBecomeSortedAndFiltered(add: IOrderPosition): void {
		const originalIndex = this.originalItems.findIndex((item) => item.orderNumber === add.orderNumber && item.position === add.position);
		if (originalIndex === -1) {
			this.originalItems = [...this.originalItems, add];
		} else {
			this.originalItems = [...this.originalItems.slice(0, originalIndex), add, ...this.originalItems.slice(originalIndex + 1)];
		}
		this.addOrderNumber(add.orderNumber);
		this.removeFromVisibleItems(add);

		if (!this.matchesCurrentFilter(add)) {
			return;
		}

		this.insertVisibleItem(add);
	}

	private insertVisibleItem(add: IOrderPosition): void {
		if (this.sortDirection === '') {
			this.items = [...this.items, add];
			return;
		}

		const index = this.items.findIndex((item) => this.sort(item, add) > 0);
		this.items = index === -1 ? [...this.items, add] : [...this.items.slice(0, index), add, ...this.items.slice(index)];
	}

	private removeFromSortedAndFiltered(remove: IOrderPosition): void {
		this.removeFromVisibleItems(remove);
		this.originalItems = this.originalItems.filter((item) => !this.isSameOrderPosition(item, remove));
		this.refreshOrderNumbers();
	}

	private removeFromVisibleItems(remove: IOrderPosition): void {
		this.items = this.items.filter((item) => !this.isSameOrderPosition(item, remove));
	}

	private updateLoadedRowVisibilities(rowKeys: Set<string>): void {
		for (const item of this.originalItems) {
			if (rowKeys.has(this.getOrderPositionKey(item))) {
				this.syncRowVisibility(item);
			}
		}
	}

	private updateRowVisibility(orderNumber: number, position: number): void {
		const item = this.originalItems.find((original) => original.orderNumber === orderNumber && original.position === position);
		if (!item) return;
		this.syncRowVisibility(item);
	}

	private syncRowVisibility(item: IOrderPosition): void {
		const index = this.items.findIndex((current) => this.isSameOrderPosition(current, item));
		const shouldShow = this.matchesCurrentFilter(item);
		if (shouldShow && index === -1) {
			this.insertVisibleItem(item);
		} else if (!shouldShow && index !== -1) {
			this.items = [...this.items.slice(0, index), ...this.items.slice(index + 1)];
		}
	}

	private isSameOrderPosition(left: IOrderPosition, right: IOrderPosition): boolean {
		return left.orderNumber === right.orderNumber && left.position === right.position;
	}

	private getOrderPositionKey(item: IOrderPosition): string {
		return this.service.getOrderPositionKey(item.orderNumber, item.position);
	}

	private addOrderNumber(orderNumber: number): void {
		const value = String(orderNumber);
		if (this.orderNumbers.includes(value)) return;
		this.orderNumbers = [...this.orderNumbers, value].sort((a, b) => Number(a) - Number(b));
	}

	private refreshOrderNumbers(): void {
		this.orderNumbers = [...new Set(this.originalItems.map((item) => String(item.orderNumber)))].sort((a, b) => Number(a) - Number(b));
	}

	onBatchSelectionChange(event: MatSelectChange): void {
		this.selectedBatchId = event.value ?? '';
		this.applySortAndFilter();
	}

	onOrderNumberSelectionChange(event: MatSelectChange): void {
		this.selectedOrderNumber = event.value ?? '';
		this.applySortAndFilter();
	}

	getCellValue(item: IOrderPosition, dataSource: string): string | number {
		return dataSource === 'batchId' ? this.service.getBatchName(item.batchId) : (dataSource.split('.').reduce((acc, key) => acc?.[key], item as any) ?? '');
	}

	private getRowStatus(item: IOrderPosition): TerminalStatusState {
		const relevantStatuses = this.service.getTerminalStatusesForCell(item.orderNumber, item.position, this.allStatusTerminalIds);
		return this.service.computeCellStatus(relevantStatuses);
	}

	openNotes(item: IOrderPosition, event: MouseEvent): void {
		event.stopPropagation();
		this.dialog.open(ProductionPlanNotesDialogComponent, {
			width: '950px',
			data: { item },
		});
	}

	openInfoDialogue(item: IOrderPosition, terminalIds?: IStatusColumn['terminalIds']): void {
		const terminalStatuses = (terminalIds ? this.service.getTerminalStatusesForCell(item.orderNumber, item.position, terminalIds) : this.service.getTerminalStatusesForOrderPosition(item.orderNumber, item.position)).map((terminalStatus) => ({
			...terminalStatus,
			status: this.service.computeCellStatus([terminalStatus]),
		}));

		this.dialog.open(ProductionPlanInfoDialogueComponent, {
			width: '760px',
			maxWidth: '96vw',
			height: '85vh',
			data: {
				item,
				batchName: this.service.getBatchName(item.batchId),
				infoColumns: this.getInfoColumnsForDialogue(item),
				terminalNames: this.terminalNames,
				terminalStatuses,
			},
		});
	}

	openStatusInfoDialogue(item: IOrderPosition, column: IStatusColumnView, status: TerminalStatusState, event: MouseEvent): void {
		event.stopPropagation();
		this.openInfoDialogue(item, status === TerminalStatusState.Irrelevant ? undefined : column.terminalIds);
	}

	private getInfoColumnsForDialogue(item: IOrderPosition): IProductionPlanInfoDialogueColumn[] {
		return this.columns
			.filter((column) => !column.isStatus)
			.map((column) => ({
				title: column.title,
				value: this.getCellValue(item, column.source),
			}));
	}

	startColumnResize(event: MouseEvent, columnIndex: number): void {
		event.preventDefault();
		event.stopPropagation();

		const startWidth = this.columns[columnIndex].width;
		const startX = event.pageX;
		const mouseMove = (moveEvent: MouseEvent) => {
			this.columns[columnIndex].width = Math.max(60, startWidth + (moveEvent.pageX - startX));
			this.tableWidth = this.columns.reduce((sum, column) => sum + column.width, 0);
		};

		const mouseUp = () => {
			document.removeEventListener('mousemove', mouseMove);
			document.removeEventListener('mouseup', mouseUp);

			if (!this.columns[columnIndex].isStatus) {
				const key = `${this.service.configurationId}#Columns#${columnIndex}`;

				this.localStorageService.set(key, this.columns[columnIndex].width);
			}
		};

		document.addEventListener('mousemove', mouseMove);
		document.addEventListener('mouseup', mouseUp);
	}

	onSort(column: string): void {
		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : this.sortDirection === 'desc' ? '' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
		this.applySortAndFilter();
	}

	getPlanName(planId: string): string {
		const plan = this.planLookup.get(planId);
		return plan ? `(${plan.alias}) ${plan.description}` : planId;
	}

	navigateToPlan(planId: string): void {
		void this.router.navigate(['/mes/production-plans', planId]);
	}
}
